import * as React from 'react';
import { Helmet } from 'react-helmet';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Font } from 'mm-theme-configuration/dist/interfaces';
import { WebFontsComponentProps } from '../../theming/ThemeProviderWithFonts';
import { getStylesheetByProvider } from './webFonts.utils';
import { ErrorReporting } from '../errorHandling/ErrorReporting';

interface AmpFontProps {
  fontFamily: string;
  fontFamilyClassName: string;
  defaultFontFamilyClassName: string;
}

interface LinkProps {
  fontConfiguration: Font;
}

export const FontAmpLink: React.FunctionComponent<LinkProps> = ({ fontConfiguration }) => {
  const errorReporting = React.useContext(ErrorReporting);
  try {
    return (
      <Helmet>
        <link
          rel="stylesheet"
          href={getStylesheetByProvider(fontConfiguration)}
        />
      </Helmet>
    );
  } catch (e) {
    errorReporting.reportException(e);
    return null;
  }
};

/* eslint-disable-next-line jsx-a11y/anchor-is-valid */
const createFontFamilyStylesheet = (fontConfiguration: Font) => <FontAmpLink fontConfiguration={fontConfiguration} />;

const createStyle = (fontFamily: string, defaultFontFamily: string) => StyleSheet.create({
  defaultFontFamily: {
    fontFamily: defaultFontFamily,
  },
  fontFamily: {
    fontFamily,
  },
});

export const AmpFont: React.FunctionComponent<AmpFontProps> = props => {
  const { fontFamily, fontFamilyClassName, defaultFontFamilyClassName } = props;
  return (
    // @ts-ignore TS2339: Property 'amp-font' does not exist on type 'JSX.IntrinsicElements'.
    <amp-font
      layout="nodisplay"
      font-family={fontFamily}
      on-load-add-class={fontFamilyClassName}
      on-load-remove-class={defaultFontFamilyClassName}
    />
  );
};

export const AmpWebFonts: React.FunctionComponent<WebFontsComponentProps> = props => {
  const { children, fontsConfiguration, typographySizesFontFamiliesMap } = props;
  const { fontFamily } = typographySizesFontFamiliesMap.normal;
  const style = createStyle(fontFamily, typographySizesFontFamiliesMap.normal.defaultFontFamily);
  const defaultFontFamilyClassName = css(style.defaultFontFamily);
  const fontFamilyClassName = css(style.fontFamily);
  return (
    <React.Fragment>
      <Helmet>
        <script async={undefined} custom-element="amp-font" src="https://cdn.ampproject.org/v0/amp-font-0.1.js" />
        {/* eslint-disable-next-line jsx-a11y/html-has-lang */}
        <html className={defaultFontFamilyClassName} />
      </Helmet>
      {createFontFamilyStylesheet(fontsConfiguration.find(({ family }) => family === fontFamily) as Font)}
      <AmpFont defaultFontFamilyClassName={defaultFontFamilyClassName} fontFamily={fontFamily} fontFamilyClassName={fontFamilyClassName} />
      {children}
    </React.Fragment>
  );
};
